import {
  Autocomplete as MUIAutocomplete,
  TextField,
  type AutocompleteProps as MUIAutocompleteProps,
} from '@mui/material'
import { useController } from 'react-hook-form'

type MuiAutocompleteBaseProps<T> = MUIAutocompleteProps<T, boolean, boolean, boolean>

type AutocompleteProps<T> = Omit<MuiAutocompleteBaseProps<T>, 'renderInput'> & {
  helperText?: string | React.ReactNode
  error?: boolean
  label: string
  ariaLabel?: string
  placeholder?: string
  required?: boolean
  variant: 'standard' | 'filled' | 'outlined'
  isMultiple?: boolean
}

export function Autocomplete<T>(props: AutocompleteProps<T>) {
  const { label, ariaLabel, error, required, helperText, variant, isMultiple, placeholder, ...otherProps } = props

  // Stupid MUI doesn't like this so we do it here and have an error in only one place
  const multiple = isMultiple ?? false
  return (
    <MUIAutocomplete
      {...otherProps}
      multiple={multiple}
      renderInput={(params) => (
        <TextField
          {...params}
          helperText={props.helperText}
          error={props.error}
          aria-label={props.ariaLabel}
          required={props.required}
          label={label}
          variant={variant}
          placeholder={placeholder}
        />
      )}
    />
  )
}

type CProps<T> = Omit<AutocompleteProps<T>, 'value' | 'onChange'> & {
  name: string
  createOption?: (value: string) => T | null
}

export function CAutocomplete<T>(props: CProps<T>) {
  const { name, createOption, isOptionEqualToValue, ...otherProps } = props
  const { field, fieldState } = useController({ name: props.name })

  const onLocalChange = (_event: unknown, value: unknown) => {
    let finalValue = value

    // Check if we are in "Multiple" mode (Array) and have a creator function
    if (Array.isArray(value) && createOption) {
      finalValue = value.reduce<T[]>((acc, item) => {
        let candidate: T | null = null

        // Case A: It's already an object (Existing selection)
        if (typeof item !== 'string') {
          candidate = item
        }
        // Case B: It's a string (New input) -> Attempt to convert
        else {
          candidate = createOption(item)
        }

        // 2. DEDUPLICATION LOGIC
        // Only add the candidate if it is valid AND not already in our list
        if (candidate) {
          const isDuplicate = acc.some((existing) => {
            // Use the comparator passed from parent, or fallback to strict equality
            if (isOptionEqualToValue) {
              return isOptionEqualToValue(existing, candidate!)
            }
            return JSON.stringify(existing) === JSON.stringify(candidate)
          })

          if (!isDuplicate) {
            acc.push(candidate)
          }
        }

        return acc
      }, [])
    }

    field.onChange(finalValue)
  }

  const error = !!fieldState.error
  const helperText = fieldState.error?.message || otherProps.helperText || undefined

  return (
    <Autocomplete {...otherProps} value={field.value} onChange={onLocalChange} error={error} helperText={helperText} />
  )
}
